/**
 * trxFieldStyles — the CANONICAL stylesheet handed to the TRX PayPage-Fields
 * library for the interior of its cross-origin field iframes.
 *
 * `stylesCSS` is the ONLY knob TRX gives us inside those frames: we cannot
 * query, reposition, or restyle their DOM from the host page because it is
 * cross-origin. Whatever is expressed here is the entire contract.
 *
 * EVERY DECLARATION CARRIES `!important`, AND THAT IS NOT STYLE PREFERENCE.
 * ----------------------------------------------------------------------
 * The library injects this sheet as a plain `<style>` appended to the frame's
 * `<head>` (verbatim from the shipped bundle: `action: "updateStyles"` ->
 * `document.head.appendChild(style)`). Landing last in the document only wins
 * a SPECIFICITY TIE — it does not beat a more specific rule.
 *
 * TRX rebuilt the production PayPage-Fields app on 2026-08-18 (every asset on
 * paypage-fields.trxservices.com carries `last-modified: 2026-08-18 10:12-10:15
 * UTC`) and wrapped the control in a new element:
 *
 *     <div class="wrapper-input"><div class="input-container"><input>
 *
 * Their stylesheet moved with it, to `.wrapper-input .input-container>input`
 * — specificity (0,2,1). The selectors below are `.wrapper-input input`
 * (0,1,1) and bare `input` (0,0,1), which is what TRX's own documentation
 * still names as the hook (`.wrapper-input input { ... }`, developer portal
 * captured 2026-08-29 — their docs are behind their build). So overnight
 * every plain declaration here LOST: height 26px -> 44px, border 0 -> 1px
 * #b2bcca with a 4px radius, background transparent -> white, padding 0 ->
 * 11px 13px, our font stack -> Nunito Sans. The fields reverted to stock TRX
 * chrome inside our slab, on every surface at once, with nothing red anywhere.
 *
 * The tell, and the reason `!important` is the right instrument rather than a
 * bigger selector: the ONLY rules that survived that rebuild were the ones
 * already flagged `!important` — the card-number artwork suppression below
 * and the error colour. Matching their selector instead would put us back to
 * winning on source order alone, which the next wrapper element takes away
 * again. `!important` is immune to both. TRX uses it in their own sheet for
 * the same reason (`.google-pay-button`, `:disabled`, `.btn:focus-visible`).
 *
 * The declarations themselves are UNCHANGED from what shipped before
 * 2026-08-18 — only their weight moved. Nothing new is asserted here, so the
 * restored rendering is the one the slab was designed against, not a fresh
 * one. In particular `.wrapper-input`'s own box (margins) is deliberately
 * still left to TRX: the frame reports `document.body.scrollHeight` back to
 * the parent, which sizes the iframe AND our container from it, so resetting
 * a margin we never owned would move geometry this change is meant to restore.
 *
 * WHY THIS MODULE EXISTS
 * ----------------------
 * This CSS was copy-pasted into four places (checkout, management billing,
 * and both shared field components). The card-number artwork suppression
 * below was then fixed in TWO of those copies and never reached the third,
 * so `<TrxCardFields>` — the shared component behind the Books invoice
 * "Charge card" dialog and the restaurant appointment-deposit flow —
 * shipped with the brand logo tiling across the card-number input, hiding
 * the digits as the operator typed them. Every surface now imports from
 * here so a fix cannot land in one copy and miss the others.
 *
 * @see apps/checkout/src/components/TrxJsLibraryInput.tsx
 * @see apps/management/src/app/billing/payment-method/_components/trxFieldSlab.tsx
 */

/**
 * Borderless, transparent input that reads as a seamless cell of the host
 * slab. Used as-is for expiration-date, cvv, and both ACH fields.
 */
export const TRX_FIELD_CSS_BASE = `
:root{color-scheme:light}
body{margin:0!important;padding:0!important;background:transparent!important;overflow:hidden!important}
.wrapper-input input,.wrapper-input select,input,select{
  width:100%!important;height:26px!important;padding:0!important;margin:0!important;
  border:0!important;outline:0!important;
  background:transparent!important;box-sizing:border-box!important;appearance:none!important;
  font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif!important;
  font-size:15px!important;line-height:26px!important;font-weight:500!important;color:#16181D!important;
  font-variant-numeric:tabular-nums!important;letter-spacing:0.01em!important;
}
.wrapper-input input::placeholder,input::placeholder{color:#B4B8C2!important;font-weight:450!important;letter-spacing:normal!important}
input:-webkit-autofill{-webkit-text-fill-color:#16181D!important;transition:background-color 9999s ease-out}
.wrapper-input .error,input.error,.error{color:#E5484D!important}
`.trim();

/**
 * The card-number field. Adds tracking, and — critically — suppresses the
 * library's own in-iframe card-network artwork.
 *
 * THE BUG THIS PREVENTS. The moment the library recognises the brand it
 * paints that brand as a background on the card-number input. It supplies
 * the image at a priority our stylesheet cannot outrank with a plain
 * declaration, while {@link TRX_FIELD_CSS_BASE}'s `background:transparent`
 * SHORTHAND resets the longhands that would have tamed it — `background-repeat`
 * falls back to `repeat` and `background-size` to `auto`. The result is the
 * brand logo TILED edge-to-edge across the field, painted over the digits.
 * On Amex it renders as a wall of "AMERICAN EXPRESS" wordmarks and the
 * cardholder cannot read what they are typing.
 *
 * So a plain `background:transparent` is not merely insufficient here — it is
 * what turns a single misplaced logo into a tiled one. The `!important`
 * declarations below are required to beat the library's own rule. The TRX
 * bundle validates this stylesheet with csstree, whose serializer preserves
 * `!important`, so the flag survives into the iframe.
 *
 * The base sheet's shorthand now carries `!important` too (see the module
 * header), which incidentally covers this case as well. That is redundancy,
 * NOT a replacement: the base shorthand blanks the image, while the block
 * below also blanks the WRAPPER and hides artwork the library injects as
 * ELEMENTS rather than as a background. Deleting either one on the grounds
 * that "the other already handles it" reopens half the bug.
 *
 * Suppressing rather than repositioning is forced: the frame is cross-origin
 * and this stylesheet is the only reachable surface. Hosts render their own
 * mark in host DOM instead — see {@link TrxCardBrandMark} — which is crisper,
 * sits where they want it, and is driven by the library's `onCardTypeChange`.
 *
 * Blanking backgrounds and hiding decorative imagery wholesale is safe: the
 * card-number iframe contains only the card-number input.
 */
export const TRX_FIELD_CSS_NUMBER = `${TRX_FIELD_CSS_BASE}
.wrapper-input input,input{letter-spacing:0.045em!important;font-weight:550!important}
html,body,form,div,span,.wrapper-input,.wrapper-input input,input,select{
  background-image:none!important;background-color:transparent!important;
}
img,svg,i,.cardtype,.card-type,[class*="cardType"],[class*="card-type"],[class*="card_type"],[class*="brand"],[class*="logo"],[class*="icon"]{
  display:none!important;
}`.trim();
